import { TridentCampaignFormData } from 'omega-rtc/types/ucd/campaign';
import _ from 'lodash';
import { createCityCondition, createCurrencyCondition, createTaxiTypeCondition } from 'omega-rtc/utils/condition-utils';


export const FIELD_MANDATORY_CONDITION = 'mandatoryCondition';
export const FIELD_SELECTED_TAXI = 'selectedTaxiTypeIDs';
export const FIELD_IS_ALL_TAXI_SELECTED = 'isAllTaxiTypesSelected';
export const FIELD_SELECTED_CITY = 'selectedPickupCities';
export const FIELD_IS_ALL_CITY_SELECTED = 'isAllCitiesSelected';
export const FIELD_SELECTED_CURRENCY = 'selectedCurrency';


export function getMandatoryConditionFields(formData: TridentCampaignFormData) {
  const mandatoryCondition = _.get(formData, FIELD_MANDATORY_CONDITION);
  const selectedPickupCities = _.get(mandatoryCondition, FIELD_SELECTED_CITY, []);
  const selectedTaxiTypeIDs = _.get(mandatoryCondition, FIELD_SELECTED_TAXI, []);
  const isAllTaxiTypesSelected = _.get(mandatoryCondition, FIELD_IS_ALL_TAXI_SELECTED, false);
  const isAllCitiesSelected = _.get(mandatoryCondition, FIELD_IS_ALL_CITY_SELECTED, false);
  const selectedCurrency = _.get(mandatoryCondition, FIELD_SELECTED_CURRENCY, []);

  return { selectedPickupCities, selectedTaxiTypeIDs, isAllTaxiTypesSelected, isAllCitiesSelected, selectedCurrency };
}


// Combining 'Mandatory Conditions' and Conditions build on 'Rule Builder'
// Mandatory conditions are cities, selectedPickupCities and currency. And only applicable for certain events
// Return fmt e.g: {lhs: var.passenger.id, operator: "eq", rhs:"977834956"}
export function constructConditionFormData(formData: TridentCampaignFormData) {
  let conditions = [];

  // Mandatory Conditions
  const {
    selectedPickupCities,
    selectedTaxiTypeIDs,
    isAllTaxiTypesSelected,
    isAllCitiesSelected,
    selectedCurrency,
  } = getMandatoryConditionFields(formData);

  // Mandatory City Condition
  if (!_.isEmpty(selectedPickupCities) && !isAllCitiesSelected) {
    conditions = [...conditions, createCityCondition(selectedPickupCities)];
  }

  // Mandatory Taxi Type Condition
  if (!_.isEmpty(selectedTaxiTypeIDs) && !isAllTaxiTypesSelected) {
    conditions = [...conditions, createTaxiTypeCondition(selectedTaxiTypeIDs)];
  }

  // Mandatory Currency Condition
  if (!_.isEmpty(selectedCurrency)) {
    conditions = [...conditions, createCurrencyCondition(selectedCurrency)];
  }

  // Rule Builder Conditions
  const ruleBuilderCondition = _.get(formData, 'condition', {});
  if (!_.isEmpty(ruleBuilderCondition)) {
    conditions = [...conditions, ruleBuilderCondition];
  }

  let combinedCondition;
  if (conditions.length === 1) {
    combinedCondition = conditions[0];
  } else if (conditions.length > 1) {
    combinedCondition = {
      operator: 'and',
      conditions,
    };
  }
  return combinedCondition;
}

export function constructLimitFormData(limitPayload: any) {
  // If limit field is 0, means the unlimited checkbox was ticked during campaign creation
  const limit = _.cloneDeep(limitPayload);
  if (_.get(limit, 'total', 0) === 0) {
    _.set(limit, 'isTotalUnlimited', true);
  }
  if (_.get(limit, 'totalPerUser', 0) === 0) {
    _.set(limit, 'isTotalPerUserUnlimited', true);
  }
  if (_.get(limit, 'totalDaily', 0) === 0) {
    _.set(limit, 'isTotalDailyUnlimited', true);
  }
  if (_.get(limit, 'totalPerUserDaily', 0) === 0) {
    _.set(limit, 'isTotalPerUserDailyUnlimited', true);
  }

  return limit;
}

// conditionPayload format: {lhs:"", operator:"", rhs:"", conditions:[] }
export function constructConditionFormPayload(conditionPayload: any) {
  if (_.isEmpty(_.get(conditionPayload, 'operator'))) {
    return {};
  }

  // Add mandatory conditions to 'mandatoryCondition' field
  let childConditions = _.get(conditionPayload, 'conditions', []);
  const mandatoryCondition = !_.isEmpty(childConditions)
    ? buildMandatoryCondition(childConditions)
    : buildMandatoryCondition([conditionPayload]);


  // Exclude mandatory conditions vars from 'condition' field
  const mandatoryConditionVars = ['var.ride.pickupCityID', 'var.ride.serviceID', 'var.payment.currency'];
  let condition = {};
  if (_.isEmpty(childConditions)) {
    // Remove the condition if it only has single condition and it's belong to mandatory conditions variable
    condition = mandatoryConditionVars.indexOf(conditionPayload.lhs) === -1 ? conditionPayload : {};
  } else {
    // Remove mandatory conditions from children
    childConditions = _.filter(childConditions, (cond => mandatoryConditionVars.indexOf(_.get(cond, 'lhs')) === -1));


    // Contains Multiple conditions but only 1 Non Mandatory Condition, return the single condition object
    if (childConditions.length === 1) {
      condition = childConditions[0];
    }

    // Contains Multiple Non Mandatory Condition, return the original condition object and remove mandatory condition vars from the children
    if (childConditions.length > 1) {
      condition = conditionPayload;
      _.set(condition, 'conditions', childConditions);
    }
  }

  return { condition, mandatoryCondition };
}

export function isFieldModified(prevFormData: TridentCampaignFormData, updatedFormData: TridentCampaignFormData, fieldName: string) {
  return !_.isEmpty(_.get(updatedFormData, fieldName)) &&
    _.get(updatedFormData, fieldName) !== _.get(prevFormData, fieldName);
}

function buildMandatoryCondition(conditions: any[]) {
  const mandatoryCondition = {};

  conditions.forEach(condition => {
    const lhs = _.get(condition, 'lhs', '');
    switch (lhs) {
      case 'var.ride.pickupCityID':
        _.set(mandatoryCondition, FIELD_SELECTED_CITY, _.get(condition, 'rhs', []));
        break;
      case 'var.ride.serviceID':
        _.set(mandatoryCondition, FIELD_SELECTED_TAXI, _.get(condition, 'rhs', []));
        break;
      case 'var.payment.currency':
        _.set(mandatoryCondition, FIELD_SELECTED_CURRENCY, _.get(condition, 'rhs', ''));
        break;
      default :
        break;
    }
  });
  return mandatoryCondition;
}

